// @flow
import React, { useState, useEffect, useRef } from 'react'
import classnames from 'classnames'
import Image from 'next/image'
import styles from './parallax.module.scss'
import isInViewport from '@utils/is-in-viewport'

interface Props {
  className?: string
  src: string
  mobileSrc?: string
  alt?: string
}

const Parallax = ({ className = '', src, mobileSrc = null, alt = '' }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [ parallax, setParallax ] = useState(0)
  const [ mounted, setMounted ] = useState(false)

  useEffect(() => {
    const onScroll = () => {
      if (!containerRef.current || !isInViewport(containerRef.current)) {
        return
      }
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      const windowHeight = window.innerHeight

      const containerTop = containerRef.current.getBoundingClientRect().top
      const containerBottom = containerTop + containerRef.current.offsetHeight

      if (containerTop - windowHeight > 0 || containerBottom < 0) {
        return
      }

      setParallax((scrollTop - containerTop) / 4)
    }

    window.addEventListener('scroll', onScroll)
    window.addEventListener('touchmove', onScroll)
    window.addEventListener('resize', onScroll)

    onScroll()
    setMounted(true)

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('touchmove', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  return (
    <div
      className={classnames(className, styles.container, {
        [styles.mounted]: mounted,
      })}
      ref={containerRef}
    >
      <picture>
        <source srcSet={src} media="(min-width: 768px)" />
        <Image
          src={mobileSrc || src}
          alt={alt}
          className={styles.parallax}
          width={1500}
          height={1200}
          style={{
            transform: `translate3d(0, ${parallax}px, 0)`,
          }}
          priority
        />
      </picture>
    </div>
  )
}

export default Parallax
